// Copyright (C) Lembani Sakala - All rights reserved.
// Lembani Sakala.
// Unauthorized copying of this file, via any medium is strictly prohibited.
// Proprietary and confidential.

import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  FaCar,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaUserFriends,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaArrowLeft,
  FaCreditCard,
  FaSuitcase,
} from 'react-icons/fa';

import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { loadVehicleTypesAsync, selectVehicleTypeById } from '../../../app/vehicle-type/vehicle-typeSlice';

import logo from '../../../images/logo.png';
import { ClipLoader } from 'react-spinners';

import { formatDateTime } from '../../../utils/datetimeFomatter';
import { BookingModalRental } from '../car-rental/BookingModalRental';
import { createBookingAsync } from '../../../app/booking/bookingSlice';
import jwtDecode from 'jwt-decode';
import { getUserAsync } from '../../../app/user/userSlice';
import { IDecodedUser } from '../../../interfaces/user';
import { getToken } from '../../../utils/localStorage';
import { RootState } from '../../../app/store';
import { createTokenAsync } from '../../../app/payment/paymentSlice';

const DPO_URL = process.env.REACT_APP_DPO_URL;

export const UserQuotePreview: React.FC<any> = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { id } = useParams();
  const { userInfo } = useAppSelector((state) => state.user);

  const tk = getToken();
  let d: IDecodedUser;
  let uid = 0;
  if (tk !== null) {
    d = jwtDecode(tk);
    uid = d.sub;
  }

  const booking = userInfo?.bookings.find((rental: any) => rental.id === Number(id));

  let currency = '';
  if (booking?.selectedRate === 'ZMW') {
    currency = 'K';
  } else if (booking?.selectedRate === 'USD') {
    currency = '$';
  }

  useEffect(() => {
    dispatch(getUserAsync(uid));
  }, [dispatch, uid]);

  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [vehTyp, setVehTyp] = useState(0);

  const handleModalOpen = (v: any) => {
    setVehTyp(v);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setVehTyp(0);
  };

  const handleBookingSubmit = (values: any) => {
    setLoading(true);
    dispatch(createBookingAsync(values));
    setTimeout(() => {
      navigate('/booking-preview');
    }, 3000);
    setShowModal(false);
    setVehTyp(0);
  };

  const { paymentToken } = useAppSelector((state: RootState) => state.payment);

  const handlePay = async (id: number) => {
    try {
      const req = await dispatch(createTokenAsync(id));
      navigate('/processing');
      if (req.meta.requestStatus === 'fulfilled') {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        window.location.href = DPO_URL + req.payload?.transToken;
      }
    } catch (error) {
      console.error('Error while handling payment:', error);
    }
  };

  const { vehicleTypes } = useAppSelector((state: RootState) => state.vehicleType);

  useEffect(() => {
    dispatch(loadVehicleTypesAsync());
    setTimeout(() => {
      setLoading(false);
    }, 3000);
  }, [dispatch]);

  const r = parseInt(booking?.vehicleType);
  const createdDate = new Date(booking?.createdAt);
  const updatedDate = new Date(booking?.updatedAt);
  const { formattedDate, formattedTime } = formatDateTime(createdDate);
  const { formattedDate: formattedUpdatedDate, formattedTime: formattedUpdatedTime } = formatDateTime(updatedDate);

  const startDateTime = new Date(booking?.startDate);
  const endDateTime = new Date(booking?.endDate);
  const { formattedDate: startDate, formattedTime: startTime } = formatDateTime(startDateTime);
  const { formattedDate: endDate, formattedTime: endTime } = formatDateTime(endDateTime);

  const vt = useAppSelector((state) => selectVehicleTypeById(state, r));

  const isPaid = booking?.payment?.find(
    (payment: { status: string }) => payment.status === 'Transaction Paid'
  );

  const getStatusDisplay = () => {
    if (booking?.payment?.length === 0) return booking?.status;
    if (isPaid) return '50% Paid';
    return booking?.payment?.[0]?.status;
  };

  const getStatusClass = () => {
    if (isPaid) return 'history-badge-success';
    if (booking?.payment?.length === 0) return 'history-badge-info';
    return 'history-badge-secondary';
  };

  try {
    if (!id || booking === undefined) {
      return (
        <div className='history-empty'>
          <FaCar className='history-empty-icon' />
          <h4>No Quote to show</h4>
          <p>This quotation may have been removed or doesn't exist.</p>
          <Link to='/' className='quote-btn quote-btn-primary mt-3'>
            Go Home
          </Link>
        </div>
      );
    }

    if (loading) {
      return (
        <div className='col-md-5 text-center mx-auto py-4'>
          <ClipLoader size={55} color='#e81f29' className='mx-auto red-txt' />
          <h3 className='text-muted'>Fetching Quote...</h3>
        </div>
      );
    }

    const totalAmount = booking?.fuelCost !== null
      ? (
          parseFloat(booking?.bookingRateAmount) * parseFloat(booking?.duration) +
          parseFloat(booking?.totalDriverAllowance) +
          parseFloat(booking?.tollFees) +
          parseFloat(booking?.busFare) +
          parseFloat(booking?.driverFood) +
          parseFloat(booking?.driverLodging) +
          parseFloat(booking?.accessoriesTotal || 0) -
          parseFloat(booking?.discount)
        ).toFixed(2)
      : (
          parseFloat(booking?.bookingRateAmount) * parseFloat(booking?.duration) +
          parseFloat(booking?.totalDriverAllowance) +
          parseFloat(booking?.accessoriesTotal || 0) -
          parseFloat(booking?.discount)
        ).toFixed(2);

    const halfAmount = (parseFloat(totalAmount) / 2).toFixed(2);

    return (
      <>
        {/* Back button */}
        <Link to='/dashboard/quotations' className='quote-btn quote-btn-secondary mb-3'>
          <FaArrowLeft className='me-1' /> Back to Quotations
        </Link>

        <div className='quote-container'>
          {/* Quote Header */}
          <div className='quote-header'>
            <div className='quote-header-left'>
              <img src={logo} className='quote-logo' alt='City Drive' />
              <div className='quote-company-info'>
                <span>citydrive.reservations@gmail.com</span>
                <span>+260 976 760 159</span>
              </div>
            </div>
            <div className='quote-header-right'>
              <h2 className='quote-number'>Quotation #{booking?.bookingNumber}</h2>
              <div className='quote-meta'>
                <span>Issued: {formattedDate} at {formattedTime}</span>
                {(formattedDate !== formattedUpdatedDate || formattedTime !== formattedUpdatedTime) && (
                  <span>Updated: {formattedUpdatedDate} at {formattedUpdatedTime}</span>
                )}
              </div>
              <span className={`history-badge ${getStatusClass()}`}>
                {getStatusDisplay()}
              </span>
              {isPaid && (
                <div style={{ fontSize: '0.75rem', color: '#888', marginTop: '4px' }}>
                  Remaining 50% due on vehicle pickup
                </div>
              )}
            </div>
          </div>

          {/* Client & Booking Info */}
          <div className='quote-info-grid'>
            <div className='quote-card'>
              <h4 className='quote-card-title'><FaUser /> Client Details</h4>
              <div className='quote-detail-list'>
                <div className='quote-detail-item'>
                  <FaUser className='quote-detail-icon' />
                  <span>{booking?.firstname} {booking?.lastname}</span>
                </div>
                <div className='quote-detail-item'>
                  <FaEnvelope className='quote-detail-icon' />
                  <span>{booking?.email}</span>
                </div>
                <div className='quote-detail-item'>
                  <FaPhone className='quote-detail-icon' />
                  <span>{booking?.phone}</span>
                </div>
              </div>
            </div>

            <div className='quote-card'>
              <h4 className='quote-card-title'><FaCar /> Booking Information</h4>
              <div className='quote-detail-list'>
                <div className='quote-detail-item'>
                  <strong>Vehicle:</strong>
                  <span>{booking?.vehicleType?.type || vt?.type}</span>
                </div>
                <div className='quote-detail-item'>
                  <strong>Route:</strong>
                  <span>
                    {booking?.pickUpTown === booking?.drivingToTown ? (
                      <>{booking?.pickUpTown} (Within town)</>
                    ) : (
                      <>
                        {booking?.pickUpTown} &rarr; {booking?.drivingToTown}
                        {booking?.drivingToCountry !== 'Zambia' && (
                          <small className='ms-1'>({booking?.drivingToCountry})</small>
                        )}
                      </>
                    )}
                  </span>
                </div>
                <div className='quote-detail-item'>
                  <strong>Drop-off:</strong>
                  <span>{booking?.dropOffTown}</span>
                </div>
                <div className='quote-detail-item'>
                  <FaCalendarAlt className='quote-detail-icon' />
                  <span>{startDate} {startTime} &mdash; {endDate} {endTime} ({booking?.duration} days)</span>
                </div>
                <div className='quote-detail-row'>
                  <div className='quote-detail-item'>
                    <strong>Drive Mode:</strong>
                    <span>{booking?.driveMode}</span>
                  </div>
                  <div className='quote-detail-item'>
                    <FaUserFriends className='quote-detail-icon' />
                    <span>{booking?.numOfAdults} Adults, {booking?.numOfChildren} Children</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Invoice Table */}
          <div className='quote-card quote-invoice-card'>
            <h4 className='quote-card-title'><FaCreditCard /> Invoice Summary</h4>
            <div className='quote-invoice-table'>
              <div className='quote-invoice-header'>
                <span className='qi-item'>Service Item</span>
                <span className='qi-rate'>Rate</span>
                <span className='qi-duration'>Duration</span>
                <span className='qi-subtotal'>Sub-total</span>
              </div>

              <div className='quote-invoice-row'>
                <span className='qi-item'>Car Rental</span>
                <span className='qi-rate'>{currency} {booking?.bookingRateAmount}</span>
                <span className='qi-duration'>{booking?.duration} days</span>
                <span className='qi-subtotal'>
                  {currency} {(parseFloat(booking?.bookingRateAmount) * parseFloat(booking?.duration)).toFixed(2)}
                </span>
              </div>

              <div className='quote-invoice-row'>
                <span className='qi-item'>Driver Allowance</span>
                <span className='qi-rate'>
                  {booking?.driveMode === 'Self-drive' ? '—' : `${currency} ${booking?.driverRateAmount}`}
                </span>
                <span className='qi-duration'>
                  {booking?.driveMode === 'Self-drive' ? '—' : `${booking?.duration} days`}
                </span>
                <span className='qi-subtotal'>
                  {booking?.driveMode === 'Self-drive'
                    ? `${currency} 0.00`
                    : `${currency} ${booking?.totalDriverAllowance}`}
                </span>
              </div>

              {booking?.fuelCost !== null && booking?.fuelCost > 0 && (
                <div className='quote-invoice-row'>
                  <span className='qi-item'>One Way Rental Cost</span>
                  <span className='qi-rate'>
                    {currency}{' '}
                    {(
                      parseFloat(booking?.fuelCost) +
                      parseFloat(booking?.tollFees) +
                      parseFloat(booking?.busFare) +
                      parseFloat(booking?.driverFood) +
                      parseFloat(booking?.driverLodging)
                    ).toFixed(2)}
                  </span>
                  <span className='qi-duration'>—</span>
                  <span className='qi-subtotal'>
                    {currency}{' '}
                    {(
                      parseFloat(booking?.fuelCost) +
                      parseFloat(booking?.tollFees) +
                      parseFloat(booking?.busFare) +
                      parseFloat(booking?.driverFood) +
                      parseFloat(booking?.driverLodging)
                    ).toFixed(2)}
                  </span>
                </div>
              )}

              {booking?.accessoriesTotal > 0 && (
                <div className='quote-invoice-row'>
                  <span className='qi-item'>
                    <FaSuitcase className='me-1' /> Accessories
                    <small className='quote-accessories-detail'>
                      ({booking?.accessories?.map((acc: string, idx: number) => (
                        <span key={acc}>
                          {acc} - K
                          {acc === 'Jerry Cans' && 25}
                          {acc === 'Satellite Phone' && 250}
                          {acc === 'GPS' && 150}
                          {acc === 'Cooler Box' && 25}
                          {acc === 'Ground tent / Mattresses / Beddings' && 900}
                          {acc === 'Camping Equipments' && 800}
                          {idx < booking?.accessories?.length - 1 && ', '}
                        </span>
                      ))})
                    </small>
                  </span>
                  <span className='qi-rate'></span>
                  <span className='qi-duration'></span>
                  <span className='qi-subtotal'>{currency} {booking?.accessoriesTotal}</span>
                </div>
              )}

              <div className='quote-invoice-row quote-discount-row'>
                <span className='qi-item'></span>
                <span className='qi-rate'></span>
                <span className='qi-duration'>Discount (7%)</span>
                <span className='qi-subtotal'>— {currency} {booking?.discount}</span>
              </div>

              <div className='quote-invoice-total'>
                <span>Total</span>
                <span>{currency} {totalAmount}</span>
              </div>

              {/* Payment info rows */}
              {isPaid && (
                <>
                  <div className='quote-invoice-row' style={{ background: '#e0f2e9' }}>
                    <span className='qi-item'></span>
                    <span className='qi-rate'></span>
                    <span className='qi-duration' style={{ fontWeight: 600 }}>Amount Paid (50%)</span>
                    <span className='qi-subtotal' style={{ fontWeight: 600, color: '#2d7a4f' }}>{currency} {halfAmount}</span>
                  </div>
                  <div className='quote-invoice-row' style={{ background: '#fff3cd' }}>
                    <span className='qi-item'></span>
                    <span className='qi-rate'></span>
                    <span className='qi-duration' style={{ fontWeight: 600 }}>
                      Remaining Balance
                      <small style={{ display: 'block', fontWeight: 400, fontStyle: 'italic' }}>due on vehicle pickup</small>
                    </span>
                    <span className='qi-subtotal' style={{ fontWeight: 600, color: '#856404' }}>{currency} {halfAmount}</span>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className='quote-footer'>
            Thank you for considering <span className='red-txt'>City Drive</span>!
          </div>
        </div>

        <BookingModalRental vt={vehTyp} show={showModal} onHide={handleModalClose} onSubmit={handleBookingSubmit} />
      </>
    );
  } catch (error) {
    console.error('Error in UserQuotePreview:', error);
    return (
      <div className='history-empty'>
        <h4>Sorry! Something went wrong while generating your quote.</h4>
        <p>It's not your fault.</p>
        <Link to='/car-rental/long-and-short-term' className='quote-btn quote-btn-primary mt-3'>
          Please try again
        </Link>
      </div>
    );
  }
};
